import { AxiosRequestConfig } from 'axios';
import { axiosClient } from '@as-react/common-utils';
import { AppliedVoucher, BasketGetBasicResponse, BasketGetResponse } from '@as-react/common-types';
import { QueryApiOptions } from '../../types/types';
import { createUrl } from '../../utils/createUrl';
import { QueryFunctionContext } from '@tanstack/react-query';

const basketEndpoints = {
  getBasket: { client: 'api/basket/token' },
  getBasketBasic: { client: 'api/basket/basic' },
  postSavePoint: { client: 'api/basket/savepoint' },
  postCheckStockReservation: { client: 'api/basket/stock/reserve' },
  postSwitchFictiveWebshop: { client: 'api/basket/fictive/switch' },
  voucher: { client: 'api/basket/voucher' },
  sendPersonalShoppingAdviceMail: { client: 'api/personalshoppingadvice/sendmail' },
};

export type PostVoucherParams = {
  code: string;
  webcode: string;
};

export const getBasket = async (
  { servicesEndpoint, isServer, defaultRequestParams }: QueryApiOptions,
  { signal }: QueryFunctionContext
): Promise<BasketGetResponse> => {
  const url = createUrl(servicesEndpoint, basketEndpoints.getBasketBasic, isServer);

  const axiosConfig: AxiosRequestConfig = {
    method: 'GET',
    url,
    params: defaultRequestParams,
    withCredentials: true,
    signal,
  };

  const result = await axiosClient.request(axiosConfig);
  return result.data;
};

export const getBasketBasic = async (
  { servicesEndpoint, isServer, defaultRequestParams }: QueryApiOptions,
  { signal }: QueryFunctionContext
): Promise<BasketGetBasicResponse> => {
  const url = createUrl(servicesEndpoint, basketEndpoints.getBasketBasic, isServer);

  const axiosConfig: AxiosRequestConfig = {
    method: 'GET',
    url,
    params: defaultRequestParams,
    withCredentials: true,
    signal,
  };

  const result = await axiosClient.request(axiosConfig);
  return result.data;
};

export const postSavePoint = async ({ servicesEndpoint, isServer }: QueryApiOptions): Promise<void> => {
  const url = createUrl(servicesEndpoint, basketEndpoints.postSavePoint, isServer);
  const axiosConfig: AxiosRequestConfig = {
    method: 'POST',
    url,
    withCredentials: true,
  };

  const result = await axiosClient.request(axiosConfig);

  return result.data;
};

export const postCheckStockReservation = async ({ servicesEndpoint, isServer }: QueryApiOptions): Promise<void> => {
  const url = createUrl(servicesEndpoint, basketEndpoints.postCheckStockReservation, isServer);
  const axiosConfig: AxiosRequestConfig = {
    method: 'POST',
    url,
    withCredentials: true,
  };

  const result = await axiosClient.request(axiosConfig);

  return result.data;
};

export const postSwitchFictiveWebshop = async ({ servicesEndpoint, isServer }: QueryApiOptions): Promise<void> => {
  const url = createUrl(servicesEndpoint, basketEndpoints.postSwitchFictiveWebshop, isServer);
  const axiosConfig: AxiosRequestConfig = {
    method: 'POST',
    url,
    withCredentials: true,
  };

  const result = await axiosClient.request(axiosConfig);

  return result.data;
};

export const deleteVoucher = async (
  { servicesEndpoint, isServer, defaultRequestParams }: QueryApiOptions,
  code: string
): Promise<void> => {
  const url = createUrl(servicesEndpoint, basketEndpoints.voucher, isServer);

  const axiosConfig: AxiosRequestConfig = {
    method: 'DELETE',
    params: {
      ...defaultRequestParams,
      voucher: code,
    },
    url,
    withCredentials: true,
  };

  return axiosClient.request(axiosConfig);
};

export const postVoucher = async (
  { servicesEndpoint, isServer, defaultRequestParams }: QueryApiOptions,
  { code, webcode }: PostVoucherParams
): Promise<AppliedVoucher> => {
  const url = createUrl(servicesEndpoint, basketEndpoints.voucher, isServer);

  const axiosConfig: AxiosRequestConfig = {
    method: 'POST',
    params: {
      ...defaultRequestParams,
      voucher: code,
      ...(webcode && { controlCode: webcode }),
    },
    url,
    withCredentials: true,
  };

  const result = await axiosClient.request(axiosConfig);
  return result.data;
};

export const sendPersonalShoppingAdviceMail = async (
  { servicesEndpoint, isServer, defaultRequestParams }: QueryApiOptions,
  email: string
) => {
  const url = createUrl(servicesEndpoint, basketEndpoints.sendPersonalShoppingAdviceMail, isServer);

  const axiosConfig: AxiosRequestConfig = {
    method: 'POST',
    params: {
      ...defaultRequestParams,
      email,
    },
    url,
    withCredentials: true,
  };

  return axiosClient.request(axiosConfig);
};
